// File Name: CareersPage.jsx

import React, { useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";

import { api } from "@/lib/site";

import CareerCard from "@/components/CareerCard";
import Particles from "@/components/Particles";

import {
  Sparkles,
  Globe,
  Heart,
  BookOpen,
  ArrowRight,
  Briefcase,
  Users,
  Laptop,
  CheckCircle2,
  ShieldCheck,
  Rocket,
} from "lucide-react";

const PERKS = [
  {
    icon: Globe,
    title: "Remote-friendly",
    desc: "Work from anywhere with flexible hybrid collaboration.",
  },
  {
    icon: Sparkles,
    title: "Modern Stack",
    desc: "Work with cutting-edge tools and scalable technologies.",
  },
  {
    icon: Heart,
    title: "Health & Wellness",
    desc: "Balanced work culture with wellness-focused benefits.",
  },
  {
    icon: BookOpen,
    title: "Learning Budget",
    desc: "Annual support for courses, certifications and events.",
  },
];

const roles = [
  "Business Analyst",
  "Sr Data Engineer",
  "Java Developer",
  ".NET Developer",
  "Sr Frontend Developer",
  "Software Engineer",
  "Software Developer",
  "Scrum Master",
  "Cyber Security Architect",
  "Information Security Architect",
];

const HIGHLIGHTS = [
  {
    icon: Briefcase,
    title: "Real-world Projects",
    desc:
      "Work on enterprise-level solutions used by modern businesses worldwide.",
  },
  {
    icon: Users,
    title: "Collaborative Culture",
    desc:
      "Grow alongside talented teams focused on innovation and excellence.",
  },
  {
    icon: Laptop,
    title: "Latest Technologies",
    desc:
      "Build scalable applications using AI, cloud and modern development stacks.",
  },
];

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Enterprise Mindset",
  },
  {
    icon: Rocket,
    title: "Innovation Driven",
  },
  {
    icon: CheckCircle2,
    title: "Growth Focused",
  },
];

export default function CareersPage() {

  const [jobs, setJobs] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [currentRole, setCurrentRole] =
    useState(0);

  useEffect(() => {

    api
      .get("/jobs")
      .then((r) =>
        setJobs(r.data)
      )
      .catch(() => {})
      .finally(() =>
        setLoading(false)
      );

  }, []);

  useEffect(() => {

    const interval =
      setInterval(() => {

        setCurrentRole((prev) =>
          (prev + 1) % roles.length
        );

      }, 2200);

    return () =>
      clearInterval(interval);

  }, []);

  return (
    <>

      {/* GLOBAL STYLES */}

      <style>
        {`
          .career-bg {
            background:
              radial-gradient(circle at top right, rgba(124,58,237,0.16), transparent 35%),
              radial-gradient(circle at bottom left, rgba(168,85,247,0.12), transparent 40%),
              linear-gradient(to bottom, #ffffff, #faf7ff);
          }

          .glass-card {
            background: rgba(255,255,255,0.78);
            border: 1px solid rgba(124,58,237,0.08);
            backdrop-filter: blur(20px);
            box-shadow: 0 12px 40px rgba(124,58,237,0.08);
          }

          .purple-glow {
            position: absolute;
            border-radius: 999px;
            filter: blur(120px);
            pointer-events: none;
          }
        `}
      </style>

      {/* HERO SECTION */}

      <section
        className="
          career-bg

          relative

          pt-36
          pb-28

          overflow-hidden
        "
        data-testid="careers-hero"
      >

        <Particles count={18} />

        {/* GLOWS */}

        <div
          className="
            purple-glow

            top-[-120px]
            right-[-120px]

            w-[520px]
            h-[520px]

            bg-violet-300/25
          "
        />

        <div
          className="
            purple-glow

            bottom-[-120px]
            left-[-120px]

            w-[420px]
            h-[420px]

            bg-fuchsia-300/15
          "
        />

        {/* GRID BG */}

        <div
          className="
            absolute
            inset-0

            grid-bg

            opacity-[0.03]
          "
        />

        <div
          className="
            relative

            max-w-7xl
            mx-auto

            px-6
            md:px-8
          "
        >

          <div
            className="
              grid
              grid-cols-1
              lg:grid-cols-2

              gap-20

              items-center
            "
          >

            {/* LEFT */}

            <motion.div
              initial={{
                opacity: 0,
                y: 25,
              }}

              animate={{
                opacity: 1,
                y: 0,
              }}

              transition={{
                duration: 0.7,
              }}
            >

              {/* BADGE */}

              <div
                className="
                  inline-flex

                  items-center

                  gap-2

                  px-5
                  py-2.5

                  rounded-full

                  bg-violet-100/70

                  border
                  border-violet-200

                  text-violet-700

                  text-sm

                  font-semibold

                  shadow-sm

                  mb-7
                "
              >

                <Sparkles className="w-4 h-4" />

                Careers at CodeFix Solutions

              </div>

              {/* HEADING */}

              <h1
                className="
                  text-5xl
                  md:text-6xl
                  lg:text-7xl

                  font-bold

                  leading-[1.03]

                  tracking-tight

                  text-slate-900
                "
                style={{
                  fontFamily: "Sora",
                }}
              >

                Build your future
                <br />

                <span
                  className="
                    bg-gradient-to-r
                    from-violet-700
                    via-fuchsia-500
                    to-purple-500

                    bg-clip-text

                    text-transparent
                  "
                >

                  with innovation.

                </span>

              </h1>

              {/* ROTATING ROLE */}

              <div className="h-[70px] mt-8">

                <AnimatePresence mode="wait">

                  <motion.div
                    key={roles[currentRole]}

                    initial={{
                      opacity: 0,
                      y: 20,
                    }}

                    animate={{
                      opacity: 1,
                      y: 0,
                    }}

                    exit={{
                      opacity: 0,
                      y: -15,
                    }}

                    transition={{
                      duration: 0.45,
                    }}

                    className="
                      text-2xl
                      md:text-4xl

                      font-semibold

                      text-violet-600
                    "
                    style={{
                      fontFamily: "Sora",
                    }}
                  >

                    {roles[currentRole]}

                  </motion.div>

                </AnimatePresence>

              </div>

              {/* DESCRIPTION */}

              <p
                className="
                  mt-5

                  text-lg
                  md:text-xl

                  text-slate-600

                  leading-relaxed

                  max-w-2xl
                "
              >

                Join a fast-growing technology company delivering
                enterprise software, AI-powered platforms,
                cybersecurity solutions and scalable cloud
                applications for global businesses.

              </p>

              <p
                className="
                  mt-5

                  text-slate-500

                  leading-relaxed

                  max-w-2xl
                "
              >

                Collaborate with talented engineers, analysts
                and innovators building impactful real-world
                products using modern technologies and
                industry-leading engineering practices.

              </p>

              {/* VALUES */}

              <div
                className="
                  flex
                  flex-wrap

                  gap-4

                  mt-9
                "
              >

                {VALUES.map((item) => {

                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}

                      className="
                        flex

                        items-center

                        gap-2

                        px-4
                        py-3

                        rounded-2xl

                        bg-white/70

                        border
                        border-violet-100

                        shadow-sm
                      "
                    >

                      <Icon
                        className="
                          w-4
                          h-4

                          text-violet-600
                        "
                      />

                      <span
                        className="
                          text-sm

                          font-medium

                          text-slate-700
                        "
                      >

                        {item.title}

                      </span>

                    </div>
                  );
                })}

              </div>

              {/* BUTTONS */}

              <div
                className="
                  flex
                  flex-wrap

                  gap-4

                  mt-10
                "
              >

                <button
                  className="
                    inline-flex

                    items-center

                    gap-2

                    px-8
                    py-4

                    rounded-2xl

                    bg-gradient-to-r
                    from-violet-600
                    via-fuchsia-500
                    to-purple-500

                    text-white

                    font-semibold

                    shadow-[0_15px_35px_rgba(124,58,237,0.28)]

                    hover:scale-[1.03]

                    hover:shadow-[0_22px_55px_rgba(124,58,237,0.35)]

                    transition-all
                    duration-300
                  "
                >

                  Explore Careers

                  <ArrowRight className="w-4 h-4" />

                </button>

                <button
                  className="
                    px-8
                    py-4

                    rounded-2xl

                    border
                    border-violet-100

                    bg-white

                    text-slate-700

                    font-medium

                    hover:border-violet-400
                    hover:text-violet-700

                    hover:shadow-xl

                    transition-all
                    duration-300
                  "
                >

                  Learn More

                </button>

              </div>

            </motion.div>

            {/* RIGHT */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.96,
              }}

              animate={{
                opacity: 1,
                scale: 1,
              }}

              transition={{
                duration: 0.7,
              }}

              className="relative"
            >

              <div
                className="
                  absolute

                  -inset-6

                  bg-violet-300/20

                  blur-3xl

                  rounded-[40px]
                "
              />

              <div
                className="
                  relative

                  overflow-hidden

                  rounded-[36px]

                  border
                  border-white/40

                  shadow-[0_30px_90px_rgba(124,58,237,0.18)]
                "
              >

                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1400&auto=format&fit=crop"

                  alt="Careers"

                  className="
                    w-full
                    h-[620px]

                    object-cover

                    transition-transform
                    duration-700

                    hover:scale-105
                  "
                />

              </div>

            </motion.div>

          </div>

        </div>

      </section>

      {/* JOB OPENINGS */}

      <section
        className="
          relative

          py-28

          bg-white
        "
        data-testid="careers-jobs"
      >

        <div
          className="
            max-w-7xl
            mx-auto

            px-6
            md:px-8
          "
        >

          <div
            className="
              text-center

              max-w-3xl

              mx-auto

              mb-20
            "
          >

            <div
              className="
                text-sm

                uppercase

                tracking-[0.35em]

                text-violet-600

                font-bold

                mb-5
              "
            >

              Open Positions

            </div>

            <h2
              className="
                text-4xl
                md:text-5xl

                font-bold

                text-slate-900

                leading-tight
              "
              style={{
                fontFamily: "Sora",
              }}
            >

              Find the perfect role

              <span
                className="
                  bg-gradient-to-r
                  from-violet-700
                  via-fuchsia-500
                  to-purple-500

                  bg-clip-text

                  text-transparent
                "
              >

                {" "}for your career

              </span>

            </h2>

            <p
              className="
                mt-6

                text-lg

                text-slate-600

                leading-relaxed
              "
            >

              Explore exciting opportunities across engineering,
              consulting, cloud infrastructure and enterprise
              solutions.

            </p>

          </div>

          {loading ? (

            <div
              className="
                grid
                grid-cols-1
                md:grid-cols-2
                lg:grid-cols-3

                gap-6
              "
            >

              {Array.from({ length: 6 }).map((_, i) => (

                <div
                  key={i}

                  className="
                    h-[260px]

                    rounded-[32px]

                    bg-gradient-to-b
                    from-violet-50
                    to-white

                    animate-pulse

                    border
                    border-violet-100
                  "
                />

              ))}

            </div>

          ) : (

            <div
              className="
                grid
                grid-cols-1
                md:grid-cols-2
                lg:grid-cols-3

                gap-6
              "
            >

              {jobs.map((j, i) => (

                <CareerCard
                  key={j.id}
                  job={j}
                  index={i}
                />

              ))}

            </div>

          )}

        </div>

      </section>

      {/* WHY JOIN */}

      <section
        className="
          relative

          py-28

          overflow-hidden

          bg-[#F8F5FF]
        "
        data-testid="careers-perks"
      >

        <div
          className="
            absolute

            top-[-180px]
            left-1/2

            -translate-x-1/2

            w-[850px]
            h-[850px]

            bg-violet-300/10

            blur-[160px]

            rounded-full
          "
        />

        <div
          className="
            relative

            max-w-7xl
            mx-auto

            px-6
            md:px-8
          "
        >

          <div className="text-center mb-20">

            <div
              className="
                text-sm

                uppercase

                tracking-[0.35em]

                text-violet-600

                font-bold

                mb-5
              "
            >

              Why Join Us

            </div>

            <h2
              className="
                text-4xl
                md:text-5xl

                font-bold

                text-slate-900
              "
              style={{
                fontFamily: "Sora",
              }}
            >

              Working at CodeFix

            </h2>

            <p
              className="
                mt-6

                text-lg

                text-slate-600

                max-w-2xl

                mx-auto
              "
            >

              A culture focused on innovation, growth,
              collaboration and long-term career success.

            </p>

          </div>

          {/* PERKS */}

          <div
            className="
              grid
              grid-cols-1
              md:grid-cols-2
              lg:grid-cols-4

              gap-7
            "
          >

            {PERKS.map((p, i) => {

              const Icon = p.icon;

              return (
                <motion.div
                  key={p.title}

                  initial={{
                    opacity: 0,
                    y: 20,
                  }}

                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}

                  viewport={{
                    once: true,
                  }}

                  transition={{
                    duration: 0.5,
                    delay: i * 0.08,
                  }}

                  className="
                    group

                    glass-card

                    rounded-[32px]

                    p-8

                    text-center

                    hover:-translate-y-3

                    hover:shadow-[0_28px_70px_rgba(124,58,237,0.14)]

                    transition-all
                    duration-500
                  "
                >

                  <div
                    className="
                      w-16
                      h-16

                      rounded-2xl

                      bg-gradient-to-br
                      from-violet-600
                      via-fuchsia-500
                      to-purple-500

                      flex
                      items-center
                      justify-center

                      mx-auto

                      mb-6

                      shadow-lg
                      shadow-violet-500/20

                      group-hover:scale-110

                      transition-all
                      duration-500
                    "
                  >

                    <Icon
                      className="
                        w-7
                        h-7

                        text-white
                      "
                    />

                  </div>

                  <h3
                    className="
                      text-2xl

                      font-bold

                      text-slate-900

                      mb-4
                    "
                    style={{
                      fontFamily: "Sora",
                    }}
                  >

                    {p.title}

                  </h3>

                  <p
                    className="
                      text-slate-600

                      leading-relaxed
                    "
                  >

                    {p.desc}

                  </p>

                </motion.div>
              );
            })}

          </div>

          {/* HIGHLIGHTS */}

          <div
            className="
              grid
              grid-cols-1
              md:grid-cols-3

              gap-7

              mt-24
            "
          >

            {HIGHLIGHTS.map((item, i) => {

              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}

                  initial={{
                    opacity: 0,
                    y: 20,
                  }}

                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}

                  viewport={{
                    once: true,
                  }}

                  transition={{
                    delay: i * 0.1,
                  }}

                  className="
                    glass-card

                    rounded-[32px]

                    p-8

                    hover:-translate-y-2

                    hover:shadow-[0_24px_60px_rgba(124,58,237,0.14)]

                    transition-all
                    duration-300
                  "
                >

                  <div
                    className="
                      w-16
                      h-16

                      rounded-2xl

                      bg-violet-50

                      flex
                      items-center
                      justify-center

                      mb-6
                    "
                  >

                    <Icon
                      className="
                        w-7
                        h-7

                        text-violet-600
                      "
                    />

                  </div>

                  <h3
                    className="
                      text-2xl

                      font-bold

                      text-slate-900

                      mb-4
                    "
                    style={{
                      fontFamily: "Sora",
                    }}
                  >

                    {item.title}

                  </h3>

                  <p
                    className="
                      text-slate-600

                      leading-relaxed
                    "
                  >

                    {item.desc}

                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>

      </section>

    </>
  );
}